import i18n, { useTranslation } from '@repo/i18n'
import { ChevronsUpDown } from 'lucide-react'
import { type ChangeEvent, type Ref, useEffect, useRef, useState } from 'react'
import { z } from 'zod'
import { useMediaQuery } from '#hooks/use-media-query'
import { cn } from '#lib/utils'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList
} from '#ui/command'
import { Drawer, DrawerContent, DrawerTitle } from '#ui/drawer'
import {
	InputGroup,
	InputGroupAddon,
	InputGroupButton,
	InputGroupInput,
	InputGroupText
} from '#ui/input-group'
import { Popover, PopoverContent, PopoverTrigger } from '#ui/popover'

interface MonetaryValue {
	amount: string
	currency: string
	minorDigits: number
}

type MonetaryValueInput = Omit<MonetaryValue, 'minorDigits'> & {
	minorDigits?: number
}

type InputCurrencyNormalizeReason = 'min' | 'max'

interface InputCurrencyNormalizeEvent {
	reason: InputCurrencyNormalizeReason
	value: MonetaryValue
	previousValue: MonetaryValue
	min?: number
	max?: number
}

const DEFAULT_CURRENCIES = Intl.supportedValuesOf('currency')

/**
 * Derives the number of minor digits for an ISO 4217 currency.
 */
function getMinorDigits(currency: string) {
	try {
		return (
			new Intl.NumberFormat('en', {
				style: 'currency',
				currency
			}).resolvedOptions().maximumFractionDigits ?? 2
		)
	} catch {
		return 2
	}
}

/**
 * Converts API minor units to a major-unit number.
 */
function toMajorUnits(
	minorUnits: string,
	currency: string,
	minorDigits?: number
) {
	const digits = minorDigits ?? getMinorDigits(currency)
	return Number(minorUnits) / 10 ** digits
}

function getCurrencySymbol(locale: string, currency: string) {
	const parts = new Intl.NumberFormat(locale, {
		style: 'currency',
		currency,
		currencyDisplay: 'narrowSymbol'
	}).formatToParts(0)
	const currencyPart = parts.find((part) => part.type === 'currency')
	const symbol = currencyPart?.value ?? currency
	const currencyIndex = currencyPart ? parts.indexOf(currencyPart) : -1
	const integerIndex = parts.findIndex((part) => part.type === 'integer')
	const position: 'prefix' | 'suffix' =
		currencyIndex < integerIndex ? 'prefix' : 'suffix'

	return { symbol, position }
}

function getSeparators(locale: string, currency: string) {
	const parts = new Intl.NumberFormat(locale, {
		style: 'currency',
		currency,
		useGrouping: true
	}).formatToParts(1234.5)

	return {
		group: parts.find((part) => part.type === 'group')?.value ?? '',
		decimal: parts.find((part) => part.type === 'decimal')?.value ?? '.'
	}
}

function parseLocaleNumber(str: string, group: string, decimal: string) {
	if (!str) {
		return null
	}
	const trimmed = str.trim()
	const isNegative = trimmed.startsWith('-')
	let cleaned = isNegative ? trimmed.slice(1) : trimmed
	if (group) {
		cleaned = cleaned.replaceAll(group, '')
	}
	cleaned = cleaned.replace(decimal, '.')
	cleaned = [...cleaned]
		.filter((char) => (char >= '0' && char <= '9') || char === '.')
		.join('')
	if (!cleaned || cleaned === '.') {
		return null
	}
	const num = Number(cleaned)
	if (Number.isNaN(num)) {
		return null
	}
	return isNegative ? -num : num
}

function formatDisplay(
	value: number | null | undefined,
	locale: string,
	digits: number
) {
	if (value === null || value === undefined) {
		return ''
	}
	return new Intl.NumberFormat(locale, {
		style: 'decimal',
		minimumFractionDigits: digits,
		maximumFractionDigits: digits,
		useGrouping: true
	}).format(value)
}

/**
 * A group separator followed by fewer digits than a group holds, once, at the end of
 * an amount with no decimal separator, is the decimal separator of another locale
 * ("12345.67" typed in Italian): it is read as one, never as a hundredfold amount.
 * Applied when the field is left, so typing "12.345" one digit at a time still works.
 */
function readForeignDecimal(
	text: string,
	group: string,
	decimal: string,
	minorDigits: number
) {
	const index = group ? text.indexOf(group) : -1
	if (
		index === -1 ||
		minorDigits === 0 ||
		text.includes(decimal) ||
		text.lastIndexOf(group) !== index
	) {
		return text
	}
	const tail = text.slice(index + group.length)
	return tail.length > 0 &&
		tail.length <= minorDigits &&
		[...tail].every((char) => char >= '0' && char <= '9')
		? `${text.slice(0, index)}${decimal}${tail}`
		: text
}

function valueToMajor(value: MonetaryValueInput) {
	if (!value.amount) {
		return null
	}
	const digits = value.minorDigits ?? getMinorDigits(value.currency)
	const parsed = toMajorUnits(value.amount, value.currency, digits)
	return Number.isFinite(parsed) ? parsed : null
}

function isSupportedCurrency(currency: string) {
	try {
		new Intl.NumberFormat('en', {
			style: 'currency',
			currency
		})
		return true
	} catch {
		return false
	}
}

function isValidMonetaryValue(value: MonetaryValueInput, required: boolean) {
	if (!value.amount) {
		return !required
	}
	const amountDigits = value.amount.startsWith('-')
		? value.amount.slice(1)
		: value.amount
	return (
		Boolean(amountDigits) &&
		[...amountDigits].every((char) => char >= '0' && char <= '9') &&
		isSupportedCurrency(value.currency) &&
		(value.minorDigits === undefined ||
			value.minorDigits === getMinorDigits(value.currency))
	)
}

function buildMonetaryValue(
	major: number | null,
	currency: string,
	minorDigits: number
): MonetaryValue {
	return {
		amount:
			major === null ? '' : String(Math.round(major * 10 ** minorDigits)),
		currency,
		minorDigits
	}
}

function buildCurrencyEntries(locale: string, currencies: string[]) {
	const currencyDisplayNames = new Intl.DisplayNames([locale], {
		type: 'currency'
	})
	return currencies
		.map((code) => {
			const { symbol: entrySymbol } = getCurrencySymbol(locale, code)
			return {
				code,
				symbol: entrySymbol,
				name: currencyDisplayNames.of(code) ?? code
			}
		})
		.sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * Currency amount input with a currency selector.
 */
function InputCurrency(props: {
	value: MonetaryValueInput
	onChange: (value: MonetaryValue) => void
	onBlur?: () => void
	currencies?: string[] | ((defaultCurrencies: string[]) => string[])
	locale?: string
	min?: number
	max?: number
	allowNegative?: boolean
	onNormalize?: (event: InputCurrencyNormalizeEvent) => void
	name: string
	disabled?: boolean
	/** Shown and not written: a value someone else sets. */
	readOnly?: boolean
	className?: string
	placeholder?: string
	'aria-invalid'?: boolean
	ref?: Ref<HTMLInputElement>
}) {
	const { t, i18n: i18nInstance } = useTranslation()
	const locale = props.locale ?? i18nInstance.language
	const currency = props.value.currency
	const minorDigits = props.value.minorDigits ?? getMinorDigits(currency)
	const { symbol, position } = getCurrencySymbol(locale, currency)
	const { group, decimal } = getSeparators(locale, currency)
	const allowsNegative =
		props.allowNegative ?? (props.min !== undefined && props.min < 0)
	const [open, setOpen] = useState(false)
	const [focused, setFocused] = useState(false)
	const isDesktop = useMediaQuery({ breakpoint: 'md' })
	const selectedCurrencyRef = useRef<HTMLDivElement | null>(null)
	const [text, setText] = useState(() =>
		formatDisplay(valueToMajor(props.value), locale, minorDigits)
	)

	useEffect(() => {
		if (focused) {
			return
		}
		setText(formatDisplay(valueToMajor(props.value), locale, minorDigits))
	}, [focused, props.value, locale, minorDigits])

	useEffect(() => {
		if (!open) {
			return
		}

		const frame = window.requestAnimationFrame(() => {
			selectedCurrencyRef.current?.scrollIntoView({
				block: 'center'
			})
		})

		return () => window.cancelAnimationFrame(frame)
	}, [open])

	const currencies =
		typeof props.currencies === 'function'
			? props.currencies(DEFAULT_CURRENCIES)
			: (props.currencies ?? DEFAULT_CURRENCIES)
	const currencyEntries = buildCurrencyEntries(
		i18nInstance.language,
		currencies
	)

	function emitChange(nextMajor: number | null) {
		props.onChange(buildMonetaryValue(nextMajor, currency, minorDigits))
	}

	function handleCurrencyChange(nextCurrency: string) {
		const nextMinorDigits = getMinorDigits(nextCurrency)
		const nextMajor =
			parseLocaleNumber(text, group, decimal) ?? valueToMajor(props.value)

		setText(formatDisplay(nextMajor, locale, nextMinorDigits))
		setOpen(false)
		props.onChange({
			amount:
				nextMajor === null
					? ''
					: String(Math.round(nextMajor * 10 ** nextMinorDigits)),
			currency: nextCurrency,
			minorDigits: nextMinorDigits
		})
	}

	function handleChange(event: ChangeEvent<HTMLInputElement>) {
		// An amount pasted as written elsewhere ("€ 12.345,67") keeps only its number.
		const raw = event.currentTarget.value
			.replaceAll(symbol, '')
			.replace(/\s/g, '')
		if (!raw) {
			setText('')
			props.onChange({ amount: '', currency, minorDigits })
			return
		}

		const minusCount = [...raw].filter((char) => char === '-').length
		if (
			minusCount > 1 ||
			(raw.includes('-') && (!allowsNegative || raw.indexOf('-') !== 0))
		) {
			return
		}

		if (
			![...raw].every(
				(char) =>
					(char >= '0' && char <= '9') ||
					(minorDigits > 0 && char === decimal) ||
					(!!group && char === group) ||
					char === '-'
			)
		) {
			return
		}

		const decimalIndex = raw.lastIndexOf(decimal)
		if (decimalIndex !== -1) {
			const decimals = raw.slice(decimalIndex + decimal.length)
			if (
				decimals.length > minorDigits ||
				![...decimals].every((char) => char >= '0' && char <= '9')
			) {
				return
			}
		}

		setText(raw)
		// A text with no number in it (a lone separator) holds no amount, not the previous one.
		emitChange(parseLocaleNumber(raw, group, decimal))
	}

	function handleBlur() {
		props.onBlur?.()
		setFocused(false)
		let nextMajor = parseLocaleNumber(
			readForeignDecimal(text, group, decimal, minorDigits),
			group,
			decimal
		)
		if (nextMajor === null) {
			setText('')
			return
		}
		const previousMajor = nextMajor
		let normalizeReason: InputCurrencyNormalizeReason | null = null
		if (props.min !== undefined && nextMajor < props.min) {
			nextMajor = props.min
			normalizeReason = 'min'
		}
		if (props.max !== undefined && nextMajor > props.max) {
			nextMajor = props.max
			normalizeReason = 'max'
		}
		setText(formatDisplay(nextMajor, locale, minorDigits))
		const nextValue = buildMonetaryValue(nextMajor, currency, minorDigits)
		props.onChange(nextValue)
		if (normalizeReason) {
			props.onNormalize?.({
				reason: normalizeReason,
				value: nextValue,
				previousValue: buildMonetaryValue(
					previousMajor,
					currency,
					minorDigits
				),
				min: props.min,
				max: props.max
			})
		}
	}

	const trigger = (
		<InputGroupButton
			aria-label={t('shared:input_currency.select_currency')}
			className='h-8 font-normal md:h-6'
			disabled={props.disabled}
			onClick={isDesktop ? undefined : () => setOpen(true)}
		>
			<span className='text-muted-foreground text-xs'>{currency}</span>
			<ChevronsUpDown data-icon='inline-end' />
		</InputGroupButton>
	)

	const currencyCommand = (
		<Command className='pb-0' defaultValue={currency} key={String(open)}>
			<CommandInput
				autoFocus={isDesktop}
				inputMode='search'
				placeholder={t('shared:input_currency.search')}
			/>
			<CommandList className='max-h-none md:max-h-60'>
				<CommandEmpty>{t('shared:input_currency.empty')}</CommandEmpty>
				<CommandGroup className='pb-0'>
					{currencyEntries.map((entry) => (
						<CommandItem
							className='min-h-11 justify-start font-normal md:min-h-8'
							data-checked={currency === entry.code}
							key={entry.code}
							keywords={[entry.name, entry.symbol]}
							onSelect={() => handleCurrencyChange(entry.code)}
							ref={
								currency === entry.code
									? selectedCurrencyRef
									: undefined
							}
							value={entry.code}
						>
							<span className='w-8 shrink-0 text-muted-foreground text-xs'>
								{entry.code}
							</span>
							<span className='flex-1 truncate text-left'>
								{entry.name}
							</span>
							<span className='text-muted-foreground text-xs'>
								{entry.symbol}
							</span>
						</CommandItem>
					))}
				</CommandGroup>
			</CommandList>
		</Command>
	)

	return (
		<InputGroup
			className={cn('h-11 md:h-9', props.className)}
			data-disabled={props.disabled || undefined}
		>
			<InputGroupAddon
				align={position === 'prefix' ? 'inline-start' : 'inline-end'}
			>
				<InputGroupText>{symbol}</InputGroupText>
			</InputGroupAddon>
			<InputGroupInput
				aria-invalid={props['aria-invalid']}
				disabled={props.disabled}
				id={props.name}
				inputMode={allowsNegative ? 'text' : 'decimal'}
				name={props.name}
				onBlur={handleBlur}
				onChange={handleChange}
				onFocus={() => setFocused(true)}
				placeholder={props.placeholder}
				readOnly={props.readOnly}
				ref={props.ref}
				type='text'
				value={text}
			/>
			{/* With a single allowed currency there is nothing to choose. */}
			{currencies.length > 1 && (
				<InputGroupAddon align='inline-start'>
					{isDesktop ? (
						<Popover onOpenChange={setOpen} open={open}>
							<PopoverTrigger render={trigger} />
							<PopoverContent align='start' className='w-72 p-0'>
								{currencyCommand}
							</PopoverContent>
						</Popover>
					) : (
						<>
							{trigger}
							<Drawer onOpenChange={setOpen} open={open}>
								<DrawerContent className='p-0'>
									<DrawerTitle className='sr-only'>
										{t(
											'shared:input_currency.select_currency'
										)}
									</DrawerTitle>
									{currencyCommand}
								</DrawerContent>
							</Drawer>
						</>
					)}
				</InputGroupAddon>
			)}
		</InputGroup>
	)
}

/**
 * Creates a Zod schema for a required input currency value.
 */
function inputCurrencySchema() {
	return z
		.object({
			amount: z.string(),
			currency: z.string(),
			minorDigits: z.number().int().min(0).optional()
		})
		.refine((value) => isValidMonetaryValue(value, true), {
			message: i18n.t('shared:input_currency.validation.invalid_amount')
		})
}

/**
 * Creates a Zod schema for an optional input currency value.
 */
function optionalInputCurrencySchema() {
	return z
		.object({
			amount: z.string(),
			currency: z.string(),
			minorDigits: z.number().int().min(0).optional()
		})
		.optional()
		.refine((value) => !value || isValidMonetaryValue(value, false), {
			message: i18n.t('shared:input_currency.validation.invalid_amount')
		})
}

export {
	InputCurrency,
	inputCurrencySchema,
	optionalInputCurrencySchema,
	getMinorDigits,
	toMajorUnits,
	type MonetaryValue,
	type MonetaryValueInput,
	type InputCurrencyNormalizeEvent,
	type InputCurrencyNormalizeReason
}
